/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, jsx-a11y/img-redundant-alt */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
// 1. Added Recharts Imports
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface NGOImpactProps {
  user: User;
}

interface Project {
  id: string;
  name: string;
  summary: string;
  company_id?: string;
}

interface Metric {
  id: string;
  project_id: string;
  metric_name: string;
  target_value: number;
  unit: string;
  baseline_value?: number;
  current_value?: number;
  metric_definition_id?: string;
  progress_id?: string;
}

interface QuarterlyReport {
  id?: string;
  project_id: string;
  period: string;
  quarter: string;
  year: number;
  narrative_summary: string;
  photos?: string[];
  videos?: string[];
  beneficiary_stories?: string[];
  status?: string;
}

interface VarianceExplanation {
  metric_id: string;
  variance_percentage: number;
  explanation_note: string;
}

interface EvidenceUpload {
  type: 'photo' | 'video' | 'story';
  file?: File | null;
  url?: string;
  caption: string;
  consent_confirmation: boolean;
  location_tags?: string;
}

interface FinalImpactReport {
  final_kpi_values: { [metricId: string]: number };
  outcome_narrative: string;
  beneficiary_reach: number;
  photos?: string[];
  uc_link?: string;
  report_pdf?: File | null;
}

// 2. Added ScoreCard Component
const ScoreCard: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color = "var(--primary)" }) => (
  <div className="card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
    <div style={{ fontSize: '2rem', fontWeight: 700, color: color, marginBottom: '0.5rem' }}>{value}</div>
    <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>{label}</div>
  </div>
);

// Helper function to get signed URL for viewing/downloading files
const getSignedUrl = async (fileUrl: string, bucketName: string): Promise<string> => {
  try {
    // If URL is empty or invalid, return as-is
    if (!fileUrl || typeof fileUrl !== 'string') {
      return fileUrl;
    }

    // If it's already a public URL that might work, try it first (but don't block)
    // We'll try signed URL in parallel since public URLs often don't work for private buckets

    // Extract file path from URL
    let filePath = '';

    // If it's already just a path (no http/https), use it directly
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      filePath = fileUrl;
    } else {
      // Try different URL patterns for Supabase storage URLs
      // Pattern 1: storage/v1/object/public/[bucket]/[path]
      const publicPattern = new RegExp(`storage/v1/object/public/${bucketName}/(.+?)(?:\\?|$)`);
      const publicMatch = fileUrl.match(publicPattern);
      if (publicMatch && publicMatch[1]) {
        filePath = decodeURIComponent(publicMatch[1]);
      } else {
        // Pattern 2: storage/v1/object/sign/[bucket]/[path]
        const signPattern = new RegExp(`storage/v1/object/sign/${bucketName}/(.+?)(?:\\?|$)`);
        const signMatch = fileUrl.match(signPattern);
        if (signMatch && signMatch[1]) {
          filePath = decodeURIComponent(signMatch[1]);
        } else {
          // Pattern 3: /[bucket]/[path] (simpler pattern)
          const simplePattern = new RegExp(`/${bucketName}/(.+?)(?:\\?|$)`);
          const simpleMatch = fileUrl.match(simplePattern);
          if (simpleMatch && simpleMatch[1]) {
            filePath = decodeURIComponent(simpleMatch[1]);
          }
        }
      }
    }

    if (!filePath) {
      console.warn('Could not extract file path from URL:', fileUrl);
      // Try to use the URL as-is (might be a public URL that works)
      return fileUrl;
    }

    // Get signed URL (valid for 1 hour)
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      // If signed URL fails (e.g., file doesn't exist), try public URL as fallback
      const { data: publicData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);
      // Return the public URL or original URL as fallback
      return publicData?.publicUrl || fileUrl;
    }

    return data?.signedUrl || fileUrl;
  } catch (err) {
    console.error('Error getting signed URL:', err, 'for URL:', fileUrl);
    return fileUrl; // Return original URL as fallback
  }
};

// 3. Custom X-Axis Tick for Multi-line text
const CustomXAxisTick = ({ x, y, payload }: any) => {
  if (!payload || !payload.value) return null;
  // Break the text into words
  const words = payload.value.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  // Group words into lines, roughly 15 chars max per line to avoid too many small lines
  words.forEach((word: string) => {
    if ((currentLine + word).length > 15 && currentLine.length > 0) {
      lines.push(currentLine.trim());
      currentLine = word + ' ';
    } else {
      currentLine += word + ' ';
    }
  });
  if (currentLine.trim()) lines.push(currentLine.trim());

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={15} textAnchor="middle" fill="#666" fontSize={11}>
        {lines.map((line, index) => (
          <tspan x={0} dy={index === 0 ? 0 : 14} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

// 4. Added SignedImage Component for secure viewing
const SignedImage: React.FC<{ url: string; alt: string; style?: React.CSSProperties; onClick?: () => void }> = ({ url, alt, style, onClick }) => {
  const [src, setSrc] = useState<string>('');
  
  useEffect(() => {
    const loadUrl = async () => {
      if (!url) return;
      // Try fund-request-attachments first, then ngo-documents fallback in getSignedUrl wrapper
      const signedUrl = await getSignedUrl(url, 'fund-request-attachments');
      // If still looks like a fund-request-attachments URL and failed, maybe it's in ngo-documents
      if (signedUrl === url || signedUrl.includes('error')) {
         const fallbackUrl = await getSignedUrl(url, 'ngo-documents');
         setSrc(fallbackUrl);
      } else {
         setSrc(signedUrl);
      }
    };
    loadUrl();
  }, [url]);

  if (!src) return <div style={{ ...style, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;

  return (
    <img
      src={src}
      alt={alt}
      style={style}
      onClick={onClick}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

const NGOImpact: React.FC<NGOImpactProps> = ({ user }) => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const evidenceTables = useRef(['project_evidence', 'project_evidences']);
  const finalReportTables = useRef(['final_impact_reports', 'final_reports']);
  const evidenceTableUsed = useRef<string | null>(null);
  const finalReportTableUsed = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'quarterly' | 'variance' | 'evidence' | 'final'>('dashboard');
  const [submittedReports, setSubmittedReports] = useState<any[]>([]);

  // Quarterly Report Form
  const [quarterlyReport, setQuarterlyReport] = useState<QuarterlyReport>({
    project_id: '',
    period: '',
    quarter: 'Q1',
    year: new Date().getFullYear(),
    narrative_summary: ''
  });
  const [metricAchievedValues, setMetricAchievedValues] = useState<{ [metricId: string]: number }>({});
  const [reportPhotos, setReportPhotos] = useState<File[]>([]);
  const [reportVideos, setReportVideos] = useState<File[]>([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<string[]>([]);
  const [existingVideoUrls, setExistingVideoUrls] = useState<string[]>([]);
  const [beneficiaryStories, setBeneficiaryStories] = useState<{ story: string; name?: string }[]>([]);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [showQuarterlyModal, setShowQuarterlyModal] = useState(false);

  // Variance Form
  const [varianceExplanations, setVarianceExplanations] = useState<VarianceExplanation[]>([]);
  const [submittingVariance, setSubmittingVariance] = useState(false);
  const [showVarianceModal, setShowVarianceModal] = useState(false);

  // Evidence Upload Form
  const [evidenceUploads, setEvidenceUploads] = useState<EvidenceUpload[]>([]);
  const [submittingEvidence, setSubmittingEvidence] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [uploadedEvidence, setUploadedEvidence] = useState<any[]>([]);

  // Final Report Form
  const [finalReport, setFinalReport] = useState<FinalImpactReport>({
    final_kpi_values: {},
    outcome_narrative: '',
    beneficiary_reach: 0
  });
  const [submittingFinal, setSubmittingFinal] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [submittedFinalReports, setSubmittedFinalReports] = useState<any[]>([]);
  const [submittedVariances, setSubmittedVariances] = useState<any[]>([]);

  const getFinancialYearInfo = (date = new Date()): { quarter: string, year: number } => {
    const month = date.getMonth();
    const currentYear = date.getFullYear();
    let financialYear: number;
    let quarter: string;

    if (month >= 3) {
      financialYear = currentYear;
      if (month <= 5) quarter = 'Q1';
      else if (month <= 8) quarter = 'Q2';
      else quarter = 'Q3';
    } else {
      financialYear = currentYear - 1;
      quarter = 'Q4';
    }
    return { quarter, year: financialYear };
  };

  const fetchData = useCallback(async () => {
    if (!ngoId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch projects assigned to this NGO
      const { data: projectNgos, error: projectNgosError } = await supabase
        .from('project_ngos')
        .select(`
          project_id,
          projects:project_id (
            id,
            name,
            summary,
            company_id
          )
        `)
        .eq('ngo_id', ngoId)
        .in('status', ['accepted', 'pending', 'changes_requested', 'pre_start', 'active']);

      if (projectNgosError) throw projectNgosError;

      const allAssignedProjectIds = projectNgos?.map((pn: any) => pn.project_id) || [];

      const safeProjects: Project[] = (projectNgos || [])
        .filter((pn: any) => pn.projects !== null)
        .map((pn: any) => ({
          id: pn.projects.id,
          name: pn.projects.name,
          summary: pn.projects.summary || '',
          company_id: pn.projects.company_id
        }));

      setProjects(safeProjects);

      // Fetch metrics for and reports for all valid projects
      const activeProjectIds = safeProjects.map(p => p.id);

      if (activeProjectIds.length > 0) {
        // Fetch project_metrics to get the baseline targets and IDs
        const { data: metricsDefData, error: metricsDefError } = await supabase
          .from('project_metrics')
          .select('*')
          .in('project_id', activeProjectIds);

        if (!metricsDefError && metricsDefData) {
          // Fetch project_metrics_progress for the current achieved values
          const { data: progressData } = await supabase
            .from('project_metrics_progress')
            .select('project_id, metric_name, current_value, id')
            .in('project_id', activeProjectIds);

          // Merge: Use project_metrics as the base, and add current_value from progress
          const mergedMetrics = metricsDefData.map((metricDef: any) => {
            const progress = progressData?.find(
              (p: any) => p.project_id === metricDef.project_id &&
                p.metric_name === metricDef.metric_name
            );
            return {
              id: metricDef.id,
              progress_id: progress?.id || null,
              project_id: metricDef.project_id,
              metric_name: metricDef.metric_name,
              target_value: metricDef.target_value || 0,
              unit: metricDef.unit || '',
              current_value: progress?.current_value || 0,
              metric_definition_id: metricDef.id // Keep this for foreign key in quarterly report
            };
          });
          setMetrics(mergedMetrics);
        }

        const { data: reportsData, error: reportsError } = await supabase
          .from('quarterly_progress')
          .select('id, project_id, metric_id, quarter, year, achieved_value, notes, created_at')
          .in('project_id', activeProjectIds)
          .not('metric_id', 'is', null)
          .order('created_at', { ascending: false });

        if (reportsError) console.error('[Impact] Fetch error details:', reportsError);

        // Fetch metric details separately (metric_id references project_metrics.id)
        const metricIds = reportsData?.map((r: any) => r.metric_id).filter(Boolean) || [];
        let metricDetailsMap: { [key: string]: { metric_name: string; unit: string } } = {};
        if (metricIds.length > 0) {
          const { data: metricDetails } = await supabase
            .from('project_metrics')
            .select('id, metric_name, unit')
            .in('id', metricIds);

          if (metricDetails) {
            metricDetails.forEach((m: any) => {
              metricDetailsMap[m.id] = { metric_name: m.metric_name, unit: m.unit };
            });
          }
        }

        // Fetch uploaded evidence with table fallback
        let evidenceData: any[] | null = null;
        for (const tableName of evidenceTables.current) {
          const { data, error } = await supabase
            .from(tableName)
            .select(`
              id,
              project_id,
              evidence_data,
              created_at
            `)
            .in('project_id', activeProjectIds)
            .eq('ngo_id', ngoId)
            .order('created_at', { ascending: false });

          if (!error) {
            evidenceTableUsed.current = tableName;
            // Add project name from already loaded projects
            evidenceData = (data || []).map((ev: any) => ({
              ...ev,
              projects: safeProjects.find(p => p.id === ev.project_id) ? { name: safeProjects.find(p => p.id === ev.project_id)!.name } : { name: 'Project' }
            }));
            break;
          }
          if (!`${error.message}`.toLowerCase().includes('does not exist')) {
            throw error;
          }
        }

        if (evidenceData) {
          const parsedEvidence = evidenceData.map((ev: any) => ({
            ...ev,
            evidence_data: typeof ev.evidence_data === 'string'
              ? (() => { try { return JSON.parse(ev.evidence_data || '[]'); } catch { return []; } })()
              : (ev.evidence_data || [])
          }));
          setUploadedEvidence(parsedEvidence);
        }

        if (!reportsError && reportsData) {
          // Group by project + quarter + year
          const reportMap: { [key: string]: any } = {};
          reportsData.forEach((r: any) => {
            const key = `${r.project_id}_${r.quarter}_${r.year}`;
            if (!reportMap[key]) {
              let reportData: any = {};
              try {
                reportData = typeof r.notes === 'string' ? JSON.parse(r.notes) : (r.notes || {});
              } catch {
                reportData = {};
              }
              const project = safeProjects.find(p => p.id === r.project_id);
              reportMap[key] = {
                id: r.id,
                project_id: r.project_id,
                project_name: project?.name || 'Project',
                quarter: r.quarter,
                year: r.year,
                narrative_summary: reportData.narrative_summary || '',
                photos: reportData.photos || [],
                videos: reportData.videos || [],
                beneficiary_stories: reportData.beneficiary_stories || [],
                created_at: r.created_at,
                metrics: [],
                rowIds: [r.id]
              };
            } else {
              if (r.id) reportMap[key].rowIds.push(r.id);
            }
            if (r.metric_id && metricDetailsMap[r.metric_id]) {
              const metricDetail = metricDetailsMap[r.metric_id];
              reportMap[key].metrics.push({
                metric_id: r.metric_id,
                metric_name: metricDetail.metric_name,
                unit: metricDetail.unit,
                achieved_value: r.achieved_value
              });
            }
          });
          setSubmittedReports(Object.values(reportMap));

          // Extract variance explanations from quarterly_progress notes
          const variances: any[] = [];
          reportsData.forEach((r: any) => {
            let reportData: any = {};
            try {
              reportData = typeof r.notes === 'string' ? JSON.parse(r.notes) : (r.notes || {});
            } catch {
              reportData = {};
            }
            if (reportData.variance_explanations && Array.isArray(reportData.variance_explanations)) {
              reportData.variance_explanations.forEach((v: any) => {
                variances.push({
                  ...v,
                  project_id: r.project_id,
                  quarter: r.quarter,
                  year: r.year,
                  created_at: r.created_at
                });
              });
            }
          });
          setSubmittedVariances(variances);
        }

        // Fetch final reports with table fallback
        let finalReportsData: any[] | null = null;
        for (const tableName of finalReportTables.current) {
          const { data, error } = await supabase
            .from(tableName)
            .select(`
              id,
              project_id,
              final_kpi_values,
              outcome_narrative,
              beneficiary_reach,
              photos,
              report_pdf_url,
              created_at
            `)
            .in('project_id', activeProjectIds)
            .eq('ngo_id', ngoId)
            .order('created_at', { ascending: false });

          if (!error) {
            finalReportTableUsed.current = tableName;
            // Add project name from already loaded projects
            finalReportsData = (data || []).map((fr: any) => {
              const project = safeProjects.find(p => p.id === fr.project_id);
              return {
                ...fr,
                projects: project ? { name: project.name } : { name: 'Project' },
                final_kpi_values: typeof fr.final_kpi_values === 'string'
                  ? (() => { try { return JSON.parse(fr.final_kpi_values || '{}'); } catch { return {}; } })()
                  : (fr.final_kpi_values || {}),
                photos: Array.isArray(fr.photos) ? fr.photos : []
              };
            });
            break;
          }
          if (!`${error.message}`.toLowerCase().includes('does not exist')) {
            throw error;
          }
        }
        if (finalReportsData) {
          setSubmittedFinalReports(finalReportsData);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [ngoId]);

  useEffect(() => {
    if (user?.id && ngoId) {
      fetchData();
    }
  }, [user.id, ngoId, fetchData]);

  // 3. New Dashboard Analytics Logic
  const dashboardAnalytics = useMemo(() => {
    let totalProgressSum = 0;
    let projectCount = 0;
    const overallTargetVsAchieved: any[] = [];

    projects.forEach(project => {
      const pMetrics = metrics.filter(m => m.project_id === project.id);
      let pTotalProg = 0;
      let mCount = 0;

      pMetrics.forEach(m => {
        const current = m.current_value || 0;
        const target = m.target_value || 1;
        // Cap progress at 100% for the average calculation to prevent skew
        const prog = Math.min((current / target) * 100, 100);
        pTotalProg += prog;
        mCount++;

        // Add to Graph Data (Full name is kept; CustomXAxisTick will split it into multiple lines)
        if (overallTargetVsAchieved.length < 10) {
          overallTargetVsAchieved.push({
            name: m.metric_name,
            Target: target,
            Achieved: current
          });
        }
      });

      const avgProjectProgress = mCount > 0 ? pTotalProg / mCount : 0;
      if (mCount > 0) {
        totalProgressSum += avgProjectProgress;
        projectCount++;
      }
    });


    return {
      totalProjects: projects.length,
      totalMetrics: metrics.length,
      avgPortfolioProgress: projectCount > 0 ? (totalProgressSum / projectCount).toFixed(0) : 0,
      overallTargetVsAchieved
    };
  }, [projects, metrics]);

  // Calculate progress and variance for dashboard (Existing function)
  const calculateMetrics = () => {
    const projectMetrics: { [projectId: string]: { metrics: Metric[], progress: number, variance: number } } = {};

    projects.forEach(project => {
      const projectMetricsList = metrics.filter(m => m.project_id === project.id);
      let totalProgress = 0;
      let totalVariance = 0;
      let count = 0;

      projectMetricsList.forEach(metric => {
        if (metric.target_value > 0 && metric.current_value !== undefined) {
          const progress = (metric.current_value / metric.target_value) * 100;
          const variance = progress - 100; // Positive = over, Negative = under
          totalProgress += progress;
          totalVariance += Math.abs(variance);
          count++;
        }
      });

      projectMetrics[project.id] = {
        metrics: projectMetricsList,
        progress: count > 0 ? totalProgress / count : 0,
        variance: count > 0 ? totalVariance / count : 0
      };
    });

    return projectMetrics;
  };

  // Helper function to recalculate cumulative progress from all quarterly reports
  const syncMetricProgress = async (projectId: string) => {
    try {
      // 1. Fetch all quarterly progress for this project
      const { data: allProgress, error: progressError } = await supabase
        .from('quarterly_progress')
        .select('metric_id, achieved_value')
        .eq('project_id', projectId);

      if (progressError) throw progressError;


      // 2. Aggregate achieved values by metric_id (from project_metrics)
      const cumulativeValues: { [metricId: string]: number } = {};
      (allProgress || []).forEach((record: any) => {
        if (record.metric_id) {
          cumulativeValues[record.metric_id] = (cumulativeValues[record.metric_id] || 0) + (Number(record.achieved_value) || 0);
        }
      });


      // 3. Get metric definitions to map metric_id to metric_name
      const { data: metricDefs, error: defError } = await supabase
        .from('project_metrics')
        .select('id, metric_name, target_value, unit')
        .eq('project_id', projectId);

      if (defError) throw defError;

      // 4. Update project_metrics_progress for each metric
      for (const def of (metricDefs || [])) {
        const cumulativeValue = cumulativeValues[def.id] || 0;

        // Check if progress entry already exists
        const { data: existingProgress } = await supabase
          .from('project_metrics_progress')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', def.metric_name)
          .maybeSingle();

        if (existingProgress) {
          const { error: updateError } = await supabase
            .from('project_metrics_progress')
            .update({
              current_value: cumulativeValue,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingProgress.id);
          if (updateError) console.error(`Error updating progress for ${def.metric_name}:`, updateError);
        } else {
          const { error: insertError } = await supabase
            .from('project_metrics_progress')
            .insert({
              project_id: projectId,
              metric_name: def.metric_name,
              target_value: def.target_value,
              current_value: cumulativeValue,
              unit: def.unit,
              updated_at: new Date().toISOString()
            });
          if (insertError) console.error(`Error inserting progress for ${def.metric_name}:`, insertError);
        }
      }
    } catch (err: any) {
      console.error('Error syncing metric progress:', err);
      throw err;
    }
  };


  const handleQuarterlyReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingReport(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload new photos (keep existing ones)
      const newPhotoUrls: string[] = [];
      for (const photo of reportPhotos) {
        const fileName = `${ngoId}/${selectedProject.id}/quarterly_photos/${Date.now()}_${photo.name}`;
        
        let bucketName = 'fund-request-attachments';
        let { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(fileName, photo);
          
        if (uploadError) {
          bucketName = 'ngo-documents';
          const { error: error2 } = await supabase.storage
            .from(bucketName)
            .upload(fileName, photo);
          uploadError = error2;
        }

        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(fileName);
          newPhotoUrls.push(publicUrl);
        }
      }
      // Combine existing and new photos
      const photoUrls = [...existingPhotoUrls, ...newPhotoUrls];

      // Upload new videos (keep existing ones)
      const newVideoUrls: string[] = [];
      for (const video of reportVideos) {
        const fileName = `${ngoId}/${selectedProject.id}/quarterly_videos/${Date.now()}_${video.name}`;
        
        let bucketName = 'fund-request-attachments';
        let { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(fileName, video);
          
        if (uploadError) {
          bucketName = 'ngo-documents';
          const { error: error2 } = await supabase.storage
            .from(bucketName)
            .upload(fileName, video);
          uploadError = error2;
        }

        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(fileName);
          newVideoUrls.push(publicUrl);
        }
      }
      // Combine existing and new videos
      const videoUrls = [...existingVideoUrls, ...newVideoUrls];

      // Get project metrics
      const projectMetrics = metrics.filter(m => m.project_id === selectedProject.id);

      // Create one row per metric in quarterly_progress
      // Use metric_definition_id (from project_metrics) for the foreign key
      const progressInserts = projectMetrics.map(metric => {
        // IMPORTANT: If user didn't enter a value, it should be 0 for this quarter.
        // Do NOT use metric.current_value as default, as that represents the cumulative total.
        const achievedValue = metricAchievedValues[metric.id] ?? 0;

        // Store narrative, photos, videos, stories in notes for this metric
        const metricNotes = {
          narrative_summary: quarterlyReport.narrative_summary,
          photos: photoUrls,
          videos: videoUrls,
          beneficiary_stories: beneficiaryStories
        };

        // Use metric_definition_id (from project_metrics) for quarterly_progress.metric_id foreign key
        const metricId = (metric as any).metric_definition_id;
        if (!metricId) {
          throw new Error(`Metric ${metric.metric_name} does not have a corresponding project_metrics.id. Please run the migration script.`);
        }

        return {
          project_id: selectedProject.id,
          metric_id: metricId, // Use ID from project_metrics (foreign key requirement)
          quarter: quarterlyReport.quarter,
          year: quarterlyReport.year,
          achieved_value: achievedValue,
          notes: JSON.stringify(metricNotes),
          submitted_by: user.id
        };
      });


      // Upsert all metric progress records (update if exists, insert if new)
      for (const insert of progressInserts) {
        const { data: upsertData, error: progressError } = await supabase
          .from('quarterly_progress')
          .upsert(insert, {
            onConflict: 'project_id,metric_id,quarter,year'
          })
          .select('id');

        if (progressError) {
          console.error('[Submit] Error upserting record:', progressError);
          throw progressError;
        }
      }

      // Recalculate and sync cumulative progress to project_metrics_progress
      await syncMetricProgress(selectedProject.id);

      alert('Quarterly impact report submitted successfully!');
      setShowQuarterlyModal(false);
      setQuarterlyReport({ project_id: '', period: '', quarter: 'Q1', year: new Date().getFullYear(), narrative_summary: '' });
      setMetricAchievedValues({});
      setReportPhotos([]);
      setReportVideos([]);
      setExistingPhotoUrls([]);
      setExistingVideoUrls([]);
      setBeneficiaryStories([]);
      setSelectedProject(null);
      await fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleEditReport = (report: any) => {
    const project = projects.find(p => p.id === report.project_id);
    if (!project) return;

    setSelectedProject(project);
    setQuarterlyReport({
      project_id: report.project_id,
      period: '',
      quarter: report.quarter,
      year: report.year,
      narrative_summary: report.narrative_summary
    });

    // Map achieved values back to metric IDs in the metricAchievedValues state
    const initialValues: { [key: string]: number } = {};
    metrics.filter(m => m.project_id === report.project_id).forEach(m => {
      const reportMetric = report.metrics.find((rm: any) => rm.metric_id === (m as any).metric_definition_id);
      initialValues[m.id] = reportMetric ? reportMetric.achieved_value : 0;
    });

    setMetricAchievedValues(initialValues);
    setExistingPhotoUrls(report.photos || []);
    setExistingVideoUrls(report.videos || []);
    setBeneficiaryStories(report.beneficiary_stories || []);
    setReportPhotos([]);
    setReportVideos([]);
    setShowQuarterlyModal(true);
  };

  const handleDeleteReport = async (report: any) => {
    if (!window.confirm(`Are you sure you want to delete the report for ${report.quarter} ${report.year}?`)) {
      return;
    }

    const reportIds = report.rowIds || [report.id];

    try {
      const { error, count } = await supabase
        .from('quarterly_progress')
        .delete({ count: 'exact' })
        .in('id', reportIds);

      if (error) {
        console.error('[Delete] Supabase error:', error);
        throw error;
      }

      try {
        // Recalculate and sync cumulative progress to project_metrics_progress
        await syncMetricProgress(report.project_id);
      } catch (syncErr) {
        console.error('[Delete] Sync failed after deletion:', syncErr);
      }

      await fetchData();
      alert('Report deleted successfully');
    } catch (err: any) {
      alert(`Error deleting report: ${err.message}`);
    }
  };

  const handleVarianceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingVariance(true);
    try {
      // Store variance explanations in quarterly_progress notes as JSON
      // Find the most recent quarterly report for this project or create a new one
      const currentQuarter = getFinancialYearInfo();
      const { data: existingReport } = await supabase
        .from('quarterly_progress')
        .select('id, notes, quarter, year')
        .eq('project_id', selectedProject.id)
        .eq('quarter', currentQuarter.quarter)
        .eq('year', currentQuarter.year)
        .limit(1)
        .maybeSingle();

      let notesData: any = {};
      if (existingReport?.notes) {
        try {
          notesData = typeof existingReport.notes === 'string' ? JSON.parse(existingReport.notes) : existingReport.notes;
        } catch {
          notesData = {};
        }
      }

      // Add variance explanations to notes
      notesData.variance_explanations = varianceExplanations.map(exp => ({
        metric_id: exp.metric_id,
        variance_percentage: exp.variance_percentage,
        explanation_note: exp.explanation_note
      }));

      if (existingReport) {
        // Update existing report
        await supabase
          .from('quarterly_progress')
          .update({ notes: JSON.stringify(notesData) })
          .eq('id', existingReport.id);
      } else {
        // Create a new quarterly report entry for variance storage
        // We need at least one metric_id, so get the first metric
        const firstMetric = metrics.find(m => m.project_id === selectedProject.id);
        if (firstMetric && firstMetric.metric_definition_id) {
          await supabase
            .from('quarterly_progress')
            .insert({
              project_id: selectedProject.id,
              metric_id: firstMetric.metric_definition_id,
              quarter: currentQuarter.quarter,
              year: currentQuarter.year,
              achieved_value: 0,
              notes: JSON.stringify(notesData)
            });
        }
      }

      alert('Variance explanations submitted successfully!');
      setShowVarianceModal(false);
      setVarianceExplanations([]);
      setSelectedProject(null);
      await fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingVariance(false);
    }
  };

  const handleEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingEvidence(true);
    try {
      const evidenceData: any[] = [];

      for (const evidence of evidenceUploads) {
        let fileUrl = evidence.url || '';
        if (evidence.file) {
          const fileName = `${ngoId}/${selectedProject.id}/evidence/${evidence.type}_${Date.now()}_${evidence.file.name}`;
          
          let bucketName = 'fund-request-attachments';
          let { error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(fileName, evidence.file);
            
          if (uploadError) {
            bucketName = 'ngo-documents';
            const { error: error2 } = await supabase.storage
              .from(bucketName)
              .upload(fileName, evidence.file);
            uploadError = error2;
          }

          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from(bucketName)
              .getPublicUrl(fileName);
            fileUrl = publicUrl;
          }
        }

        evidenceData.push({
          type: evidence.type,
          url: fileUrl,
          caption: evidence.caption,
          consent_confirmation: evidence.consent_confirmation,
          location_tags: evidence.location_tags
        });
      }

      const evidenceTable = evidenceTableUsed.current || evidenceTables.current[0];
      await supabase
        .from(evidenceTable)
        .insert({
          project_id: selectedProject.id,
          company_id: selectedProject.company_id || null,
          evidence_data: evidenceData
        });

      alert('Evidence uploaded successfully!');
      setShowEvidenceModal(false);
      setEvidenceUploads([]);
      setSelectedProject(null);
      await fetchData(); // Refresh to show uploaded evidence
      fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingEvidence(false);
    }
  };

  const handleFinalReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingFinal(true);
    try {
      // Upload final report PDF if provided
      let pdfUrl = '';
      if (finalReport.report_pdf) {
        const fileName = `${ngoId}/${selectedProject.id}/final_report_${Date.now()}_${finalReport.report_pdf.name}`;
        
        let bucketName = 'fund-request-attachments';
        let { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(fileName, finalReport.report_pdf);
          
        if (uploadError) {
          bucketName = 'ngo-documents';
          const { error: error2 } = await supabase.storage
            .from(bucketName)
            .upload(fileName, finalReport.report_pdf);
          uploadError = error2;
        }

        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(fileName);
          pdfUrl = publicUrl;
        }
      }

      // Create final impact report with table fallback
      const finalTable = finalReportTableUsed.current || finalReportTables.current[0];
      await supabase
        .from(finalTable)
        .insert({
          project_id: selectedProject.id,
          company_id: selectedProject.company_id || null,
          final_kpi_values: finalReport.final_kpi_values,
          outcome_narrative: finalReport.outcome_narrative,
          beneficiary_reach: finalReport.beneficiary_reach,
          photos: finalReport.photos || [],
          uc_link: finalReport.uc_link,
          report_pdf_url: pdfUrl,
          status: 'submitted'
        });

      alert('Final impact report submitted successfully!');
      setShowFinalModal(false);
      setFinalReport({
        final_kpi_values: {},
        outcome_narrative: '',
        beneficiary_reach: 0
      });
      setSelectedProject(null);
      fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingFinal(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const metricsData = calculateMetrics();

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem' }}>Impact</h1>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid var(--border)' }}>
        {(['dashboard', 'quarterly', 'variance', 'evidence', 'final'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab ? '600' : '400',
              cursor: 'pointer',
              textTransform: 'capitalize',
              marginBottom: '-2px'
            }}
          >
            {tab === 'dashboard' ? 'Impact Dashboard' : tab === 'quarterly' ? 'Quarterly Reports' : tab === 'variance' ? 'Variance Explanation' : tab === 'evidence' ? 'Evidence Uploads' : 'Final Report'}
          </button>
        ))}
      </div>

      {/* 4. UPDATED IMPACT DASHBOARD TAB */}
      {activeTab === 'dashboard' && (
        <div>
          {/* Scorecards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <ScoreCard label="Total Projects" value={dashboardAnalytics.totalProjects} color="#3b82f6" />
            <ScoreCard label="KPIs Tracked" value={dashboardAnalytics.totalMetrics} color="#8b5cf6" />
            <ScoreCard label="Avg Progress" value={`${dashboardAnalytics.avgPortfolioProgress}%`} color={parseFloat(dashboardAnalytics.avgPortfolioProgress.toString()) > 75 ? '#10b981' : '#f59e0b'} />
          </div>

          {/* Graph: Target vs Achieved */}
          <div className="card" style={{ padding: '1.5rem', minHeight: '400px', marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem', color: '#374151' }}>Key Metrics: Target vs. Achieved</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardAnalytics.overallTargetVsAchieved} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" interval={0} tick={<CustomXAxisTick />} height={60} />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="Target" fill="#fbbf24" name="Target" />
                <Bar dataKey="Achieved" fill="#10b981" name="Achieved" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {projects.length === 0 && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No projects assigned yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Quarterly Reports Tab */}
      {activeTab === 'quarterly' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Quarterly / Milestone Impact Reporting</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Report on each KPI and add supporting evidence and stories.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowQuarterlyModal(true);
                setSelectedProject(null);
                setQuarterlyReport({ project_id: '', period: '', quarter: 'Q1', year: new Date().getFullYear(), narrative_summary: '' });
                setMetricAchievedValues({});
                setReportPhotos([]);
                setReportVideos([]);
                setExistingPhotoUrls([]);
                setExistingVideoUrls([]);
                setBeneficiaryStories([]);
              }}
            >
              + Submit Quarterly Report
            </button>
          </div>

          {/* Display Submitted Reports */}
          {submittedReports.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Submitted Reports</h3>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {submittedReports.map((report, idx) => (
                  <div key={idx} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                      <div>
                        <h4>{report.project_name}</h4>
                        <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                          {report.quarter} {report.year} • {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleEditReport(report)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleDeleteReport(report)}
                          style={{ color: '#ef4444', borderColor: '#ef4444' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {report.narrative_summary && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Summary:</strong>
                        <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{report.narrative_summary}</p>
                      </div>
                    )}
                    {report.metrics && report.metrics.length > 0 && (
                      <div className="table-container" style={{ marginBottom: '1rem' }}>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              <th>Achieved Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.metrics.map((m: any, i: number) => (
                              <tr key={i}>
                                <td><strong>{m.metric_name}</strong></td>
                                <td>{m.achieved_value?.toLocaleString('en-IN') || '0'} {m.unit || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Display Photos */}
                    {report.photos && report.photos.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Photos:</strong>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                          {report.photos.map((photo: string, idx: number) => (
                            <div key={idx} style={{ position: 'relative' }}>
                              <SignedImage
                                url={photo}
                                alt={`Photo ${idx + 1}`}
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  border: '1px solid #e5e7eb'
                                }}
                                onClick={async () => {
                                   let viewUrl = await getSignedUrl(photo, 'fund-request-attachments');
                                   if (viewUrl === photo) viewUrl = await getSignedUrl(photo, 'ngo-documents');
                                   window.open(viewUrl, '_blank');
                                }}
                              />
                              <button
                                onClick={async () => {
                                  let viewUrl = await getSignedUrl(photo, 'fund-request-attachments');
                                  if (viewUrl === photo) viewUrl = await getSignedUrl(photo, 'ngo-documents');
                                  window.open(viewUrl, '_blank');
                                }}
                                className="btn btn-link"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  marginTop: '0.25rem',
                                  fontSize: '0.875rem',
                                  color: 'var(--primary)',
                                  textAlign: 'center',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                View Full Size
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Display Videos */}
                    {report.videos && report.videos.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Videos:</strong>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                          {report.videos.map((video: string, idx: number) => (
                            <button
                              key={idx}
                              onClick={async () => {
                                let viewUrl = await getSignedUrl(video, 'fund-request-attachments');
                                if (viewUrl === video) viewUrl = await getSignedUrl(video, 'ngo-documents');
                                window.open(viewUrl, '_blank');
                              }}
                              className="btn btn-sm btn-outline"
                            >
                              🎥 Video {idx + 1}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Display Beneficiary Stories */}
                    {report.beneficiary_stories && report.beneficiary_stories.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Beneficiary Stories:</strong>
                        {report.beneficiary_stories.map((story: any, idx: number) => (
                          <div key={idx} style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                            {story.name && <strong>{story.name}: </strong>}
                            <span>{story.story}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {submittedReports.length === 0 && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No quarterly reports submitted yet.</p>
            </div>
          )}

          {/* Quarterly Report Modal */}
          {showQuarterlyModal && (
            <div className="modal-overlay" onClick={() => setShowQuarterlyModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>Submit Quarterly Impact Report</h2>
                <form onSubmit={handleQuarterlyReportSubmit}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={quarterlyReport.project_id}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setQuarterlyReport({ ...quarterlyReport, project_id: e.target.value });
                        setSelectedProject(project || null);
                        // Initialize achieved values as empty (0) for new quarter report
                        // User should enter fresh values for each quarter
                        if (project) {
                          const projectMetrics = metrics.filter(m => m.project_id === project.id);
                          const initialValues: { [key: string]: number } = {};
                          projectMetrics.forEach(m => {
                            initialValues[m.id] = 0; // Start with 0, not current value
                          });
                          setMetricAchievedValues(initialValues);
                        }
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedProject && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Quarter *</label>
                          <select
                            value={quarterlyReport.quarter}
                            onChange={async (e) => {
                              const newQuarter = e.target.value;
                              setQuarterlyReport({ ...quarterlyReport, quarter: newQuarter });

                              // Load existing values for this quarter/year if they exist
                              if (selectedProject) {
                                const projectMetrics = metrics.filter(m => m.project_id === selectedProject.id);
                                const { data: existingReports } = await supabase
                                  .from('quarterly_progress')
                                  .select('metric_id, achieved_value, notes')
                                  .eq('project_id', selectedProject.id)
                                  .eq('quarter', newQuarter)
                                  .eq('year', quarterlyReport.year);

                                if (existingReports && existingReports.length > 0) {
                                  // Load existing values for this quarter
                                  const existingValues: { [key: string]: number } = {};
                                  let loadedNarrative = '';
                                  let loadedPhotos: string[] = [];
                                  let loadedVideos: string[] = [];
                                  let loadedStories: { story: string; name?: string }[] = [];

                                  // Get data from first report (all metrics share the same narrative/photos/videos/stories)
                                  const firstReport = existingReports[0];
                                  if (firstReport.notes) {
                                    try {
                                      const notesData = typeof firstReport.notes === 'string'
                                        ? JSON.parse(firstReport.notes)
                                        : firstReport.notes;

                                      loadedNarrative = notesData.narrative_summary || '';
                                      loadedPhotos = notesData.photos || [];
                                      loadedVideos = notesData.videos || [];
                                      loadedStories = notesData.beneficiary_stories || [];
                                    } catch (e) {
                                      console.error('Error parsing notes:', e);
                                    }
                                  }

                                  projectMetrics.forEach(m => {
                                    const metricId = (m as any).metric_definition_id;
                                    const existing = (existingReports as any[]).find((r: any) => (r as any).metric_id === metricId);
                                    if (existing) {
                                      existingValues[m.id] = existing.achieved_value || 0;
                                    } else {
                                      existingValues[m.id] = 0; // New metric, start with 0
                                    }
                                  });

                                  setMetricAchievedValues(existingValues);
                                  setQuarterlyReport(prev => ({ ...prev, narrative_summary: loadedNarrative }));
                                  setExistingPhotoUrls(loadedPhotos);
                                  setExistingVideoUrls(loadedVideos);
                                  setBeneficiaryStories(loadedStories);
                                  setReportPhotos([]); // Clear new file uploads
                                  setReportVideos([]); // Clear new file uploads
                                } else {
                                  // No existing report for this quarter, start fresh
                                  const initialValues: { [key: string]: number } = {};
                                  projectMetrics.forEach(m => {
                                    initialValues[m.id] = 0; // Start fresh for new quarter
                                  });
                                  setMetricAchievedValues(initialValues);
                                  setQuarterlyReport(prev => ({ ...prev, narrative_summary: '' }));
                                  setExistingPhotoUrls([]);
                                  setExistingVideoUrls([]);
                                  setBeneficiaryStories([]);
                                  setReportPhotos([]);
                                  setReportVideos([]);
                                }
                              }
                            }}
                            required
                          >
                            <option value="Q1">Q1 (Apr-Jun)</option>
                            <option value="Q2">Q2 (Jul-Sep)</option>
                            <option value="Q3">Q3 (Oct-Dec)</option>
                            <option value="Q4">Q4 (Jan-Mar)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Year *</label>
                          <input
                            type="number"
                            value={quarterlyReport.year}
                            onChange={async (e) => {
                              const newYear = parseInt(e.target.value);
                              setQuarterlyReport({ ...quarterlyReport, year: newYear });

                              // Load existing values for this quarter/year if they exist
                              if (selectedProject && !isNaN(newYear)) {
                                const projectMetrics = metrics.filter(m => m.project_id === selectedProject.id);
                                const { data: existingReports } = await supabase
                                  .from('quarterly_progress')
                                  .select('metric_id, achieved_value, notes')
                                  .eq('project_id', selectedProject.id)
                                  .eq('quarter', quarterlyReport.quarter)
                                  .eq('year', newYear);

                                if (existingReports && existingReports.length > 0) {
                                  // Load existing values for this quarter/year
                                  const existingValues: { [key: string]: number } = {};
                                  let loadedNarrative = '';
                                  let loadedPhotos: string[] = [];
                                  let loadedVideos: string[] = [];
                                  let loadedStories: { story: string; name?: string }[] = [];

                                  // Get data from first report (all metrics share the same narrative/photos/videos/stories)
                                  const firstReport = existingReports[0];
                                  if (firstReport.notes) {
                                    try {
                                      const notesData = typeof firstReport.notes === 'string'
                                        ? JSON.parse(firstReport.notes)
                                        : firstReport.notes;

                                      loadedNarrative = notesData.narrative_summary || '';
                                      loadedPhotos = notesData.photos || [];
                                      loadedVideos = notesData.videos || [];
                                      loadedStories = notesData.beneficiary_stories || [];
                                    } catch (e) {
                                      console.error('Error parsing notes:', e);
                                    }
                                  }

                                  projectMetrics.forEach(m => {
                                    const metricId = (m as any).metric_definition_id;
                                    const existing = existingReports.find(r => r.metric_id === metricId);
                                    if (existing) {
                                      existingValues[m.id] = existing.achieved_value || 0;
                                    } else {
                                      existingValues[m.id] = 0; // New metric, start with 0
                                    }
                                  });

                                  setMetricAchievedValues(existingValues);
                                  setQuarterlyReport(prev => ({ ...prev, narrative_summary: loadedNarrative }));
                                  setExistingPhotoUrls(loadedPhotos);
                                  setExistingVideoUrls(loadedVideos);
                                  setBeneficiaryStories(loadedStories);
                                  setReportPhotos([]); // Clear new file uploads
                                  setReportVideos([]); // Clear new file uploads
                                } else {
                                  // No existing report for this quarter/year, start fresh
                                  const initialValues: { [key: string]: number } = {};
                                  projectMetrics.forEach(m => {
                                    initialValues[m.id] = 0; // Start fresh for new quarter/year
                                  });
                                  setMetricAchievedValues(initialValues);
                                  setQuarterlyReport(prev => ({ ...prev, narrative_summary: '' }));
                                  setExistingPhotoUrls([]);
                                  setExistingVideoUrls([]);
                                  setBeneficiaryStories([]);
                                  setReportPhotos([]);
                                  setReportVideos([]);
                                }
                              }
                            }}
                            required
                            min="2020"
                            max="2100"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Period</label>
                        <input
                          type="text"
                          value={`${quarterlyReport.quarter} FY ${quarterlyReport.year}-${(quarterlyReport.year + 1).toString().slice(-2)}`}
                          readOnly
                          style={{ background: '#f9fafb', cursor: 'not-allowed' }}
                        />
                        <small style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                          Automatically generated from Quarter and Year
                        </small>
                      </div>

                      {/* Metrics Progress */}
                      {metrics.filter(m => m.project_id === selectedProject.id).map((metric) => {
                        const achievedValue = metricAchievedValues[metric.id] ?? metric.current_value ?? 0;
                        const targetValue = metric.target_value || 1;
                        const variance = ((achievedValue / targetValue) * 100) - 100;

                        return (
                          <div key={metric.id} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                            <h4>{metric.metric_name} {metric.unit ? `(${metric.unit})` : ''}</h4>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Target</label>
                                <input
                                  type="number"
                                  value={metric.target_value || 0}
                                  disabled
                                  style={{ background: '#f3f4f6' }}
                                />
                              </div>
                              <div className="form-group">
                                <label>Achieved Value *</label>
                                <input
                                  type="number"
                                  value={achievedValue}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    setMetricAchievedValues(prev => ({
                                      ...prev,
                                      [metric.id]: value
                                    }));
                                  }}
                                  placeholder="Enter achieved value"
                                  required
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              <div className="form-group">
                                <label>Progress</label>
                                <input
                                  type="text"
                                  value={`${((achievedValue / targetValue) * 100).toFixed(1)}% of target`}
                                  disabled
                                  style={{
                                    background: '#f3f4f6',
                                    color: ((achievedValue / targetValue) * 100) >= 100 ? '#10b981' : ((achievedValue / targetValue) * 100) >= 75 ? '#f59e0b' : '#ef4444'
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className="form-group">
                        <label>Narrative Summary *</label>
                        <textarea
                          value={quarterlyReport.narrative_summary}
                          onChange={(e) => setQuarterlyReport({ ...quarterlyReport, narrative_summary: e.target.value })}
                          required
                          rows={5}
                          placeholder="Provide a comprehensive narrative summary of the impact achieved..."
                        />
                      </div>

                      <div className="form-group">
                        <label>Photos</label>
                        {/* Display existing photos */}
                        {existingPhotoUrls.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <strong style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>Existing Photos:</strong>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                              {existingPhotoUrls.map((url, idx) => (
                                <div key={idx} style={{ position: 'relative' }}>
                                  <img
                                    src={url}
                                    alt={`Existing photo ${idx + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '80px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #e5e7eb'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setExistingPhotoUrls(existingPhotoUrls.filter((_, i) => i !== idx))}
                                    style={{
                                      position: 'absolute',
                                      top: '4px',
                                      right: '4px',
                                      background: 'rgba(239, 68, 68, 0.9)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => setReportPhotos(Array.from(e.target.files || []))}
                        />
                        <small style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                          {reportPhotos.length > 0 && `${reportPhotos.length} new photo(s) selected`}
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Videos</label>
                        {/* Display existing videos */}
                        {existingVideoUrls.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <strong style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>Existing Videos:</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                              {existingVideoUrls.map((url, idx) => (
                                <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline"
                                    style={{ marginRight: '0.25rem' }}
                                  >
                                    🎥 Video {idx + 1}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => setExistingVideoUrls(existingVideoUrls.filter((_, i) => i !== idx))}
                                    style={{
                                      background: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '0.25rem 0.5rem',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          multiple
                          accept="video/*"
                          onChange={(e) => setReportVideos(Array.from(e.target.files || []))}
                        />
                        <small style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                          {reportVideos.length > 0 && `${reportVideos.length} new video(s) selected`}
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Beneficiary Stories</label>
                        {beneficiaryStories.map((story, index) => (
                          <div key={index} className="card" style={{ marginBottom: '0.5rem', padding: '1rem' }}>
                            <input
                              type="text"
                              placeholder="Beneficiary name (optional)"
                              value={story.name || ''}
                              onChange={(e) => {
                                const updated = [...beneficiaryStories];
                                updated[index].name = e.target.value;
                                setBeneficiaryStories(updated);
                              }}
                              style={{ marginBottom: '0.5rem' }}
                            />
                            <textarea
                              placeholder="Story content"
                              value={story.story}
                              onChange={(e) => {
                                const updated = [...beneficiaryStories];
                                updated[index].story = e.target.value;
                                setBeneficiaryStories(updated);
                              }}
                              rows={3}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => setBeneficiaryStories(beneficiaryStories.filter((_, i) => i !== index))}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => setBeneficiaryStories([...beneficiaryStories, { story: '' }])}
                        >
                          + Add Story
                        </button>
                      </div>
                    </>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowQuarterlyModal(false);
                        setSelectedProject(null);
                        setQuarterlyReport({ project_id: '', period: '', quarter: 'Q1', year: new Date().getFullYear(), narrative_summary: '' });
                        setMetricAchievedValues({});
                        setReportPhotos([]);
                        setReportVideos([]);
                        setExistingPhotoUrls([]);
                        setExistingVideoUrls([]);
                        setBeneficiaryStories([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingReport || !selectedProject}
                    >
                      {submittingReport ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Variance Explanation Tab */}
      {activeTab === 'variance' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Variance Explanation</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Add reasons for under-achievement or over-achievement.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowVarianceModal(true)}
            >
              + Explain Variance
            </button>
          </div>

          {/* Variance Modal */}
          {showVarianceModal && (
            <div className="modal-overlay" onClick={() => setShowVarianceModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <h2>Explain Variance</h2>
                <form onSubmit={handleVarianceSubmit} style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto', paddingRight: '0.5rem' }}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                        if (project) {
                          const projectMetrics = metrics.filter(m => m.project_id === project.id);
                          setVarianceExplanations(projectMetrics.map(m => {
                            const current = m.current_value || 0;
                            const target = m.target_value || 1;
                            const variance = ((current / target) * 100) - 100;
                            return {
                              metric_id: m.id,
                              variance_percentage: variance,
                              explanation_note: ''
                            };
                          }));
                        }
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedProject && varianceExplanations.length > 0 && (
                    <>
                      {varianceExplanations.map((explanation, index) => {
                        const metric = metrics.find(m => m.id === explanation.metric_id);
                        return (
                          <div key={explanation.metric_id} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                            <h4>{metric?.metric_name}</h4>
                            <div className="form-group">
                              <label>Variance %</label>
                              <input
                                type="text"
                                value={`${explanation.variance_percentage > 0 ? '+' : ''}${explanation.variance_percentage.toFixed(1)}%`}
                                disabled
                              />
                            </div>
                            <div className="form-group">
                              <label>Explanation Note *</label>
                              <textarea
                                value={explanation.explanation_note}
                                onChange={(e) => {
                                  const updated = [...varianceExplanations];
                                  updated[index].explanation_note = e.target.value;
                                  setVarianceExplanations(updated);
                                }}
                                required
                                rows={3}
                                placeholder="Explain the reasons for this variance..."
                              />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowVarianceModal(false);
                        setSelectedProject(null);
                        setVarianceExplanations([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingVariance || !selectedProject}
                    >
                      {submittingVariance ? 'Submitting...' : 'Submit Explanation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Display Submitted Variances */}
          {submittedVariances.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Submitted Variance Explanations</h3>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {submittedVariances.map((variance, idx) => {
                  const project = projects.find(p => p.id === variance.project_id);
                  const metric = metrics.find(m => m.id === variance.metric_id);
                  return (
                    <div key={idx} className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                          <h4>{project?.name || 'Project'}</h4>
                          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                            {metric?.metric_name || 'Metric'} • {variance.quarter} {variance.year} • {new Date(variance.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          backgroundColor: variance.variance_percentage > 0 ? '#10b98115' : '#ef444415',
                          color: variance.variance_percentage > 0 ? '#10b981' : '#ef4444'
                        }}>
                          {variance.variance_percentage > 0 ? '+' : ''}{variance.variance_percentage.toFixed(1)}%
                        </span>
                      </div>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{variance.explanation_note}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {submittedVariances.length === 0 && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center', marginTop: '2rem' }}>
              <p style={{ color: 'var(--muted)' }}>No variance explanations submitted yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Evidence Uploads Tab */}
      {activeTab === 'evidence' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Evidence Uploads</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Add media files and beneficiary stories linked to each report.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowEvidenceModal(true)}
            >
              + Upload Evidence
            </button>
          </div>

          {/* Evidence Modal */}
          {showEvidenceModal && (
            <div className="modal-overlay" onClick={() => setShowEvidenceModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>Upload Evidence</h2>
                <form onSubmit={handleEvidenceSubmit}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {evidenceUploads.map((evidence, index) => (
                    <div key={index} className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                      <div className="form-group">
                        <label>Evidence Type *</label>
                        <select
                          value={evidence.type}
                          onChange={(e) => {
                            const updated = [...evidenceUploads];
                            updated[index].type = e.target.value as 'photo' | 'video' | 'story';
                            setEvidenceUploads(updated);
                          }}
                          required
                        >
                          <option value="photo">Photo</option>
                          <option value="video">Video</option>
                          <option value="story">Beneficiary Story</option>
                        </select>
                      </div>
                      {(evidence.type === 'photo' || evidence.type === 'video') && (
                        <div className="form-group">
                          <label>Upload File</label>
                          <input
                            type="file"
                            accept={evidence.type === 'photo' ? 'image/*' : 'video/*'}
                            onChange={(e) => {
                              const updated = [...evidenceUploads];
                              updated[index].file = e.target.files?.[0] || null;
                              setEvidenceUploads(updated);
                            }}
                          />
                        </div>
                      )}
                      <div className="form-group">
                        <label>Caption *</label>
                        <textarea
                          value={evidence.caption}
                          onChange={(e) => {
                            const updated = [...evidenceUploads];
                            updated[index].caption = e.target.value;
                            setEvidenceUploads(updated);
                          }}
                          required
                          rows={2}
                        />
                      </div>
                      <div className="form-group">
                        <label>Location Tags</label>
                        <input
                          type="text"
                          value={evidence.location_tags || ''}
                          onChange={(e) => {
                            const updated = [...evidenceUploads];
                            updated[index].location_tags = e.target.value;
                            setEvidenceUploads(updated);
                          }}
                          placeholder="e.g., Mumbai, Maharashtra"
                        />
                      </div>
                      <div className="form-group">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={evidence.consent_confirmation}
                            onChange={(e) => {
                              const updated = [...evidenceUploads];
                              updated[index].consent_confirmation = e.target.checked;
                              setEvidenceUploads(updated);
                            }}
                            required
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              alignSelf: 'flex-start'
                            }}
                          />
                          <label style={{ margin: 0 }}>
                            Consent Confirmation *
                          </label>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => setEvidenceUploads(evidenceUploads.filter((_, i) => i !== index))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setEvidenceUploads([...evidenceUploads, { type: 'photo', caption: '', consent_confirmation: false }])}
                  >
                    + Add Evidence
                  </button>

                  <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowEvidenceModal(false);
                        setSelectedProject(null);
                        setEvidenceUploads([]);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingEvidence || !selectedProject || evidenceUploads.length === 0}
                    >
                      {submittingEvidence ? 'Uploading...' : 'Upload Evidence'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Display Uploaded Evidence */}
          {uploadedEvidence.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Uploaded Evidence</h3>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {uploadedEvidence.map((evidence) => {
                  const evidenceData = Array.isArray(evidence.evidence_data)
                    ? evidence.evidence_data
                    : (typeof evidence.evidence_data === 'string'
                      ? JSON.parse(evidence.evidence_data || '[]')
                      : []);

                  return (
                    <div key={evidence.id} className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                          <h4>{(evidence.projects as any)?.name || 'Project'}</h4>
                          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                            Uploaded on {new Date(evidence.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          className="btn btn-sm"
                          style={{
                            fontSize: '0.8rem',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none'
                          }}
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to delete this evidence upload? This will delete all photos, videos, and stories in this upload.')) return;
                            setSubmittingEvidence(true);
                            try {
                              const evidenceTable = evidenceTableUsed.current || evidenceTables.current[0];
                              // Delete files from storage first
                              const evidenceData = Array.isArray(evidence.evidence_data)
                                ? evidence.evidence_data
                                : (typeof evidence.evidence_data === 'string'
                                  ? JSON.parse(evidence.evidence_data || '[]')
                                  : []);

                              for (const item of evidenceData) {
                                if (item.url) {
                                  let pathInBucket = item.url;
                                  if (item.url.includes('/storage/v1/object/public/')) {
                                    const parts = item.url.split('/storage/v1/object/public/');
                                    if (parts.length > 1) {
                                      pathInBucket = parts[1].replace('ngo-documents/', '');
                                    }
                                  } else if (item.url.includes('/storage/v1/object/sign/')) {
                                    const parts = item.url.split('/storage/v1/object/sign/');
                                    if (parts.length > 1) {
                                      const pathParts = parts[1].split('/');
                                      if (pathParts.length > 1) {
                                        pathInBucket = pathParts.slice(1).join('/');
                                      }
                                    }
                                  }

                                  if (pathInBucket && pathInBucket !== item.url) {
                                    // Try deleting from both potential buckets
                                    await supabase.storage.from('fund-request-attachments').remove([pathInBucket]);
                                    await supabase.storage.from('ngo-documents').remove([pathInBucket]);
                                  }
                                }
                              }

                              // Delete from database
                              const { error: deleteError } = await supabase
                                .from(evidenceTable)
                                .delete()
                                .eq('id', evidence.id);

                              if (deleteError) throw deleteError;

                              alert('Evidence deleted successfully!');
                              await fetchData();
                            } catch (err: any) {
                              console.error('Error deleting evidence:', err);
                              alert(`Error: ${err.message || 'Failed to delete evidence'}`);
                            } finally {
                              setSubmittingEvidence(false);
                            }
                          }}
                          disabled={submittingEvidence}
                        >
                          Delete
                        </button>
                      </div>

                      <div style={{ display: 'grid', gap: '1rem' }}>
                        {evidenceData.map((item: any, idx: number) => (
                          <div key={idx} style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                              <strong style={{ textTransform: 'capitalize' }}>{item.type}</strong>
                              {item.location_tags && (
                                <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                                  📍 {item.location_tags}
                                </span>
                              )}
                            </div>
                            {item.caption && (
                              <p style={{ marginBottom: '0.5rem' }}>{item.caption}</p>
                            )}
                            {item.url && (
                              <div style={{ marginTop: '0.5rem' }}>
                                {item.type === 'photo' ? (
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      let signedUrl = await getSignedUrl(item.url, 'fund-request-attachments');
                                      if (signedUrl === item.url) signedUrl = await getSignedUrl(item.url, 'ngo-documents');
                                      window.open(signedUrl, '_blank');
                                    }}
                                  >
                                    📷 View Photo
                                  </a>
                                ) : item.type === 'video' ? (
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      let signedUrl = await getSignedUrl(item.url, 'fund-request-attachments');
                                      if (signedUrl === item.url) signedUrl = await getSignedUrl(item.url, 'ngo-documents');
                                      window.open(signedUrl, '_blank');
                                    }}
                                  >
                                    🎥 View Video
                                  </a>
                                ) : (
                                  <div style={{ padding: '0.75rem', background: 'white', borderRadius: '4px' }}>
                                    <strong>Story:</strong>
                                    <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{item.caption || 'No story content'}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            {item.consent_confirmation && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#10b981' }}>
                                ✓ Consent Confirmed
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {uploadedEvidence.length === 0 && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No evidence uploaded yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Final Impact Report Tab */}
      {activeTab === 'final' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Final Impact Report / Closure Document</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Compile and upload final impact summary for the project.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowFinalModal(true)}
            >
              + Submit Final Report
            </button>
          </div>

          {/* Final Report Modal */}
          {showFinalModal && (
            <div className="modal-overlay" onClick={() => setShowFinalModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>Submit Final Impact Report</h2>
                <form onSubmit={handleFinalReportSubmit}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                        if (project) {
                          const projectMetrics = metrics.filter(m => m.project_id === project.id);
                          const kpiValues: { [key: string]: number } = {};
                          projectMetrics.forEach(m => {
                            kpiValues[m.id] = m.current_value || 0;
                          });
                          setFinalReport({ ...finalReport, final_kpi_values: kpiValues });
                        }
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedProject && (
                    <>
                      {/* Final KPI Values */}
                      <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
                        <h3>Final KPI Values</h3>
                        {metrics.filter(m => m.project_id === selectedProject.id).map((metric) => (
                          <div key={metric.id} className="form-row" style={{ marginBottom: '0.5rem' }}>
                            <div className="form-group">
                              <label>{metric.metric_name}</label>
                              <input
                                type="number"
                                value={finalReport.final_kpi_values[metric.id] || 0}
                                onChange={(e) => {
                                  setFinalReport({
                                    ...finalReport,
                                    final_kpi_values: {
                                      ...finalReport.final_kpi_values,
                                      [metric.id]: parseFloat(e.target.value) || 0
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div className="form-group">
                              <label>Target</label>
                              <input type="number" value={metric.target_value} disabled />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="form-group">
                        <label>Outcome Narrative *</label>
                        <textarea
                          value={finalReport.outcome_narrative}
                          onChange={(e) => setFinalReport({ ...finalReport, outcome_narrative: e.target.value })}
                          required
                          rows={6}
                          placeholder="Describe the overall outcomes and impact achieved..."
                        />
                      </div>

                      <div className="form-group">
                        <label>Beneficiary Reach *</label>
                        <input
                          type="number"
                          value={finalReport.beneficiary_reach}
                          onChange={(e) => setFinalReport({ ...finalReport, beneficiary_reach: parseInt(e.target.value) || 0 })}
                          required
                          placeholder="Total number of beneficiaries reached"
                        />
                      </div>

                      <div className="form-group">
                        <label>Photos</label>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => {
                            // Handle photo uploads - would need to upload and get URLs
                            // For now, just store file references
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label>UC Link</label>
                        <input
                          type="url"
                          value={finalReport.uc_link || ''}
                          onChange={(e) => setFinalReport({ ...finalReport, uc_link: e.target.value })}
                          placeholder="Link to Utilization Certificate"
                        />
                      </div>

                      <div className="form-group">
                        <label>Final Report PDF *</label>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setFinalReport({ ...finalReport, report_pdf: e.target.files?.[0] || null })}
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowFinalModal(false);
                        setSelectedProject(null);
                        setFinalReport({ final_kpi_values: {}, outcome_narrative: '', beneficiary_reach: 0 });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingFinal || !selectedProject}
                    >
                      {submittingFinal ? 'Submitting...' : 'Submit Final Report'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Display Submitted Final Reports */}
          {submittedFinalReports.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Submitted Final Reports</h3>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {submittedFinalReports.map((report) => (
                  <div key={report.id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                      <div>
                        <h4>{(report.projects as any)?.name || 'Project'}</h4>
                        <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                          Submitted on {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Outcome Narrative:</strong>
                      <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{report.outcome_narrative || 'No narrative provided'}</p>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Beneficiary Reach:</strong>
                      <p style={{ marginTop: '0.5rem' }}>{report.beneficiary_reach?.toLocaleString() || 0} beneficiaries</p>
                    </div>

                    {Object.keys(report.final_kpi_values || {}).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Final KPI Values:</strong>
                        <div className="table-container" style={{ marginTop: '0.5rem' }}>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th>Final Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(report.final_kpi_values).map(([metricId, value]: [string, any]) => {
                                const metric = metrics.find(m => m.id === metricId);
                                return (
                                  <tr key={metricId}>
                                    <td>{metric?.metric_name || 'Unknown Metric'}</td>
                                    <td>{value} {metric?.unit || ''}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {report.photos && report.photos.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Photos:</strong>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                          {report.photos.map((photo: string, idx: number) => (
                            <div key={idx} style={{ position: 'relative' }}>
                              <SignedImage
                                url={photo}
                                alt={`Photo ${idx + 1}`}
                                style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }}
                                onClick={async () => {
                                   let viewUrl = await getSignedUrl(photo, 'fund-request-attachments');
                                   if (viewUrl === photo) viewUrl = await getSignedUrl(photo, 'ngo-documents');
                                   window.open(viewUrl, '_blank');
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {report.report_pdf_url && (
                      <div>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            let signedUrl = await getSignedUrl(report.report_pdf_url, 'fund-request-attachments');
                            if (signedUrl === report.report_pdf_url) signedUrl = await getSignedUrl(report.report_pdf_url, 'ngo-documents');
                            window.open(signedUrl, '_blank');
                          }}
                          className="btn btn-primary"
                        >
                          📄 View Final Report PDF
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {submittedFinalReports.length === 0 && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center', marginTop: '2rem' }}>
              <p style={{ color: 'var(--muted)' }}>No final reports submitted yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NGOImpact;
